import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RotateCcw, CheckCircle, XCircle } from 'lucide-react';
import { useCourse } from '../hooks/useCourse';
import { useAuth } from '../hooks/useAuth';
import { logActivity, supabase } from '../lib/supabase';
import { calculateScore } from '../lib/scoring';
import { Flashcard } from '../types';

const VOCAB_QUIZ_LENGTH = 15;

// Shuffle array helper
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Smart question selection with wrong-answer priority and rotation
function selectSmartQuestions<T extends { id: string }>(
  questions: T[],
  wrongHistory: string[],
  lastQuizQuestionIds: string[],
  count: number
): T[] {
  const questionWeights = questions.map(q => {
    const wrongCount = wrongHistory.filter(id => id === q.id).length;
    const wasInLastQuiz = lastQuizQuestionIds.includes(q.id);

    let weight = 1;
    if (wrongCount >= 3) weight = 4;
    else if (wrongCount >= 2) weight = 3;
    else if (wrongCount === 1) weight = 2;

    if (wasInLastQuiz) weight = Math.max(0.5, weight - 2);
    if (wrongCount === 0 && wasInLastQuiz) weight = 0.3;

    return { question: q, weight };
  });

  const selected: T[] = [];
  const remaining = [...questionWeights];

  while (selected.length < count && remaining.length > 0) {
    const totalWeight = remaining.reduce((sum, qw) => sum + qw.weight, 0);
    let random = Math.random() * totalWeight;
    let selectedIndex = 0;

    for (let i = 0; i < remaining.length; i++) {
      random -= remaining[i].weight;
      if (random <= 0) {
        selectedIndex = i;
        break;
      }
    }

    selected.push(remaining[selectedIndex].question);
    remaining.splice(selectedIndex, 1);
  }

  return shuffleArray(selected);
}

// Generated vocab question
interface VocabQuestion {
  id: string; // flashcard id
  flashcard: Flashcard;
  direction: 'en-de' | 'de-en';
  questionText: string;
  options: string[];
  correctIndex: number;
  correctAnswer: string;
}

// Wrong question detail for results
interface WrongVocabDetail {
  id: string;
  word: string;
  correctTranslation: string;
  direction: 'en-de' | 'de-en';
}

// Generate MC questions from flashcards
function generateVocabQuestions(
  selectedFlashcards: Flashcard[],
  allFlashcards: Flashcard[],
  courseId?: string
): VocabQuestion[] {
  const questions: VocabQuestion[] = [];
  const isDeutsch = courseId === 'deutsch-schularbeit';

  selectedFlashcards.forEach((fc, index) => {
    // Alternate direction: 50% question→answer, 50% answer→question
    const direction: 'en-de' | 'de-en' = index % 2 === 0 ? 'en-de' : 'de-en';

    // Get distractors from same category
    const sameCategory = allFlashcards.filter(
      f => f.category === fc.category && f.id !== fc.id
    );

    // Fallback to all flashcards if not enough in same category
    const distractorPool = sameCategory.length >= 3
      ? sameCategory
      : allFlashcards.filter(f => f.id !== fc.id);

    // Pick 3 random distractors
    const shuffledDistractors = shuffleArray(distractorPool).slice(0, 3);

    let questionText: string;
    let correctAnswer: string;
    let wrongAnswers: string[];

    if (direction === 'en-de') {
      // Forward: question → answer
      questionText = isDeutsch
        ? `Was passt zu "${fc.question}"?`
        : `Was bedeutet "${fc.question}"?`;
      correctAnswer = fc.answer;
      wrongAnswers = shuffledDistractors.map(d => d.answer);
    } else {
      // Reverse: answer → question
      questionText = isDeutsch
        ? `Welcher Begriff passt zu "${fc.answer}"?`
        : `Was heißt "${fc.answer}" auf Englisch?`;
      correctAnswer = fc.question;
      wrongAnswers = shuffledDistractors.map(d => d.question);
    }

    // Combine and shuffle options
    const allOptions = [correctAnswer, ...wrongAnswers];
    const shuffledOptions = shuffleArray(allOptions);
    const correctIndex = shuffledOptions.indexOf(correctAnswer);

    questions.push({
      id: fc.id,
      flashcard: fc,
      direction,
      questionText,
      options: shuffledOptions,
      correctIndex,
      correctAnswer,
    });
  });

  return questions;
}

// Wrong Questions Display Component
function WrongVocabSection({ wrongVocabs, courseId }: { wrongVocabs: WrongVocabDetail[]; courseId?: string }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (wrongVocabs.length === 0) return null;

  return (
    <div style={{
      background: 'rgba(239, 83, 80, 0.08)',
      border: '1px solid rgba(239, 83, 80, 0.2)',
      borderRadius: '16px',
      padding: '16px',
      marginBottom: '20px',
      marginTop: '16px',
    }}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <span style={{ fontSize: '20px' }}>❌</span>
          <span style={{ color: '#ef5350', fontWeight: 700, fontSize: '15px' }}>
            {wrongVocabs.length} {wrongVocabs.length === 1 ? 'Vokabel' : 'Vokabeln'} falsch
          </span>
        </div>
        <span style={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: '20px',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
        }}>
          ▼
        </span>
      </button>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '12px',
        padding: '10px 12px',
        background: 'rgba(255, 152, 0, 0.1)',
        borderRadius: '10px',
      }}>
        <span style={{ fontSize: '16px' }}>💪</span>
        <span style={{
          color: '#ff9800',
          fontSize: '13px',
          lineHeight: '1.4',
        }}>
          Diese Vokabeln kommen beim nächsten Mal häufiger dran!
        </span>
      </div>

      {isExpanded && (
        <div style={{
          marginTop: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}>
          {wrongVocabs.map((wv) => (
            <div
              key={wv.id}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '10px',
                padding: '12px',
                borderLeft: '3px solid #ef5350',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '6px',
              }}>
                <span style={{ color: '#ef5350', fontSize: '14px' }}>❌</span>
                <span style={{ color: '#e8eaf6', fontSize: '14px', fontWeight: 600 }}>
                  {wv.word}
                </span>
                <span style={{
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: '11px',
                  background: 'rgba(255,255,255,0.1)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                }}>
                  {courseId === 'deutsch-schularbeit'
                    ? (wv.direction === 'en-de' ? 'Frage→Antwort' : 'Antwort→Frage')
                    : courseId === 'spanisch-schularbeit'
                    ? (wv.direction === 'en-de' ? 'ES→DE' : 'DE→ES')
                    : (wv.direction === 'en-de' ? 'EN→DE' : 'DE→EN')}
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <span style={{ color: '#4caf50', fontSize: '14px' }}>✅</span>
                <span style={{ color: '#4caf50', fontSize: '14px' }}>
                  {wv.correctTranslation}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function VocabQuiz() {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const { course, isLoading, error } = useCourse(courseId || '');
  const { user } = useAuth();

  const [quizKey, setQuizKey] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongIds, setWrongIds] = useState<string[]>([]);
  const [wrongDetails, setWrongDetails] = useState<WrongVocabDetail[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());

  // Smart selection state
  const [wrongHistory, setWrongHistory] = useState<string[]>([]);
  const [lastQuizIds, setLastQuizIds] = useState<string[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Direction tracking for logging
  const [enDeCount, setEnDeCount] = useState(0);
  const [deEnCount, setDeEnCount] = useState(0);

  // Load quiz history
  useEffect(() => {
    async function loadHistory() {
      if (!user?.id || !courseId) {
        setHistoryLoaded(true);
        return;
      }

      try {
        const { data } = await supabase
          .from('quiz_results')
          .select('wrong_questions, question_ids')
          .eq('user_id', user.id)
          .eq('course_id', courseId)
          .eq('mode', 'vocab-quiz')
          .order('created_at', { ascending: false })
          .limit(5);

        if (data && data.length > 0) {
          const allWrong = data.flatMap(r => (r.wrong_questions as string[]) || []);
          setWrongHistory(allWrong);

          const lastIds = (data[0].question_ids as string[]) || [];
          setLastQuizIds(lastIds);
        }
      } catch (err) {
        console.error('Error loading vocab quiz history:', err);
      }

      setHistoryLoaded(true);
    }

    loadHistory();
  }, [user?.id, courseId, quizKey]);

  // Generate questions
  const questions = useMemo<VocabQuestion[]>(() => {
    if (!course?.flashcards || course.flashcards.length < 4 || !historyLoaded) {
      return [];
    }

    // Filter by category if specified
    const pool = categoryFilter
      ? course.flashcards.filter(f => f.category === categoryFilter)
      : course.flashcards;

    if (pool.length < 4) return [];

    // Select flashcards with smart weighting
    const count = Math.min(VOCAB_QUIZ_LENGTH, pool.length);
    const selectedFlashcards = selectSmartQuestions(
      pool,
      wrongHistory,
      lastQuizIds,
      count
    );

    // Generate questions from selected flashcards (use filtered pool for distractors)
    return generateVocabQuestions(selectedFlashcards, pool, courseId || undefined);
  }, [course?.flashcards, wrongHistory, lastQuizIds, historyLoaded, quizKey, categoryFilter]);

  const currentQuestion = questions[currentIndex];

  const handleSelect = (index: number) => {
    if (isAnswered) return;
    setSelectedAnswer(index);
  };

  const handleSubmit = () => {
    if (selectedAnswer === null || !currentQuestion) return;

    setIsAnswered(true);

    // Track direction
    if (currentQuestion.direction === 'en-de') {
      setEnDeCount(c => c + 1);
    } else {
      setDeEnCount(c => c + 1);
    }

    if (selectedAnswer === currentQuestion.correctIndex) {
      setCorrectCount(c => c + 1);
    } else {
      setWrongIds(w => [...w, currentQuestion.id]);
      setWrongDetails(w => [...w, {
        id: currentQuestion.id,
        word: currentQuestion.direction === 'en-de'
          ? currentQuestion.flashcard.question
          : currentQuestion.flashcard.answer,
        correctTranslation: currentQuestion.correctAnswer,
        direction: currentQuestion.direction,
      }]);
    }
  };

  const handleNext = async () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(i => i + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    } else {
      // Quiz finished - save results
      const timeSeconds = Math.round((Date.now() - startTime) / 1000);
      const percentage = Math.round((correctCount / questions.length) * 100);
      const allQuestionIds = questions.map(q => q.id);

      if (user?.id && courseId) {
        // Save to quiz_results
        await supabase.from('quiz_results').insert({
          user_id: user.id,
          course_id: courseId,
          mode: 'vocab-quiz',
          score: correctCount,
          total: questions.length,
          percentage,
          wrong_questions: wrongIds,
          question_ids: allQuestionIds,
          time_seconds: timeSeconds,
        });

        // Log activity
        await logActivity(user.id, 'vocab_quiz', {
          course_id: courseId,
          score: correctCount,
          total: questions.length,
          percentage,
          direction_split: { en_de: enDeCount, de_en: deEnCount },
          duration_minutes: Math.ceil(timeSeconds / 60),
        });
      }

      setIsFinished(true);
    }
  };

  const restart = () => {
    setQuizKey(k => k + 1);
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setCorrectCount(0);
    setWrongIds([]);
    setWrongDetails([]);
    setIsFinished(false);
    setStartTime(Date.now());
    setEnDeCount(0);
    setDeEnCount(0);
    setHistoryLoaded(false);
  };

  if (isLoading || !course || !historyLoaded) {
    return <div className="loading">Laden...</div>;
  }

  if (error) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
      </div>
    );
  }

  if (!course.flashcards || course.flashcards.length < 4) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <p style={{ color: 'rgba(255,255,255,0.6)' }}>
          Nicht genug Vokabeln für ein Quiz (mindestens 4 benötigt).
        </p>
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
      </div>
    );
  }

  // Results screen
  if (isFinished) {
    const percentage = Math.round((correctCount / questions.length) * 100);
    const result = calculateScore(correctCount, questions.length);

    return (
      <div className="quiz-result">
        <h1>{result.emoji} Vokabel-Quiz fertig!</h1>

        <div style={{
          background: 'rgba(255, 213, 79, 0.15)',
          border: '1px solid rgba(255, 213, 79, 0.3)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '16px',
          marginTop: '16px',
          textAlign: 'center',
        }}>
          <div style={{ color: '#ffd54f', fontSize: '36px', fontWeight: 700 }}>
            {correctCount}/{questions.length}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginTop: '4px' }}>
            {percentage}% richtig
          </div>
        </div>

        {/* Direction stats */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '16px',
        }}>
          <div style={{
            flex: 1,
            background: 'rgba(66, 165, 245, 0.1)',
            borderRadius: '12px',
            padding: '12px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#42a5f5', fontSize: '12px', fontWeight: 600 }}>EN → DE</div>
            <div style={{ color: '#e8eaf6', fontSize: '18px', fontWeight: 700 }}>{enDeCount}</div>
          </div>
          <div style={{
            flex: 1,
            background: 'rgba(255, 152, 0, 0.1)',
            borderRadius: '12px',
            padding: '12px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#ff9800', fontSize: '12px', fontWeight: 600 }}>DE → EN</div>
            <div style={{ color: '#e8eaf6', fontSize: '18px', fontWeight: 700 }}>{deEnCount}</div>
          </div>
        </div>

        <p className="result-message">{result.message}</p>

        {/* Questions overview */}
        {(() => {
          const totalPool = course.flashcards?.length || 0;
          const questionsInRound = questions.length;
          const wrongCount = wrongDetails.length;

          return (
            <div style={{
              background: 'rgba(66, 165, 245, 0.08)',
              border: '1px solid rgba(66, 165, 245, 0.2)',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '16px',
            }}>
              <div style={{ color: '#42a5f5', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                📊 Übersicht
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                <div>Vokabeln in dieser Runde: <strong style={{ color: '#e8eaf6' }}>{questionsInRound}</strong></div>
                <div>Davon falsch: <strong style={{ color: wrongCount > 0 ? '#ef5350' : '#66bb6a' }}>{wrongCount}</strong></div>
                <div>Gesamt-Vokabelpool: <strong style={{ color: '#e8eaf6' }}>{totalPool}</strong></div>
                {wrongCount > 0 && (
                  <div style={{ marginTop: '6px', color: '#ff9800', fontStyle: 'italic' }}>
                    → In der nächsten Runde kommen die {wrongCount} falschen Vokabeln bevorzugt wieder dran!
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Wrong vocabs section */}
        <WrongVocabSection wrongVocabs={wrongDetails} courseId={courseId || undefined} />

        <button className="btn-primary" onClick={restart}>
          <RotateCcw size={18} />
          Nochmal
        </button>
        <Link to={`/course/${courseId}`} className="btn-secondary">
          Zurück zum Kurs
        </Link>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div className="loading">Fragen werden generiert...</div>;
  }

  return (
    <div className="quiz-view">
      <div className="quiz-header">
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
        <span style={{
          background: 'rgba(156, 39, 176, 0.2)',
          color: '#ce93d8',
          padding: '6px 12px',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: 600,
        }}>
          📝 Vokabel-Quiz
        </span>
      </div>

      {/* Smart selection info banner - show only on first question */}
      {currentIndex === 0 && wrongHistory.length > 0 && (
        <div style={{
          background: 'rgba(255, 152, 0, 0.1)',
          border: '1px solid rgba(255, 152, 0, 0.25)',
          borderRadius: '12px',
          padding: '12px 14px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '13px',
        }}>
          <span style={{ fontSize: '18px' }}>🎯</span>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>
            <strong style={{ color: '#ff9800' }}>{new Set(wrongHistory).size} Vokabeln</strong> aus den letzten Runden waren falsch — sie kommen jetzt bevorzugt dran!
          </span>
        </div>
      )}

      <div className="quiz-progress">
        Frage {currentIndex + 1} von {questions.length}
        <span style={{
          marginLeft: '8px',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: 600,
          background: currentQuestion.direction === 'en-de'
            ? 'rgba(66, 165, 245, 0.2)'
            : 'rgba(255, 152, 0, 0.2)',
          color: currentQuestion.direction === 'en-de' ? '#42a5f5' : '#ff9800',
        }}>
          {courseId === 'deutsch-schularbeit'
            ? (currentQuestion.direction === 'en-de' ? 'Frage→Antwort' : 'Antwort→Frage')
            : courseId === 'spanisch-schularbeit'
            ? (currentQuestion.direction === 'en-de' ? 'ES→DE' : 'DE→ES')
            : (currentQuestion.direction === 'en-de' ? 'EN→DE' : 'DE→EN')}
        </span>
      </div>

      <div className="quiz-question">
        <h2>{currentQuestion.questionText}</h2>
      </div>

      <div className="quiz-options">
        {currentQuestion.options.map((option, index) => {
          let className = 'quiz-option';
          if (isAnswered) {
            if (index === currentQuestion.correctIndex) {
              className += ' correct';
            } else if (index === selectedAnswer) {
              className += ' wrong';
            }
          } else if (index === selectedAnswer) {
            className += ' selected';
          }

          return (
            <button
              key={index}
              className={className}
              onClick={() => handleSelect(index)}
              disabled={isAnswered}
            >
              {option}
              {isAnswered && index === currentQuestion.correctIndex && (
                <CheckCircle size={18} />
              )}
              {isAnswered && index === selectedAnswer && index !== currentQuestion.correctIndex && (
                <XCircle size={18} />
              )}
            </button>
          );
        })}
      </div>

      {isAnswered && (
        <div className="quiz-explanation">
          <p>
            <strong>{currentQuestion.flashcard.question}</strong> = {currentQuestion.flashcard.answer}
          </p>
        </div>
      )}

      <div className="quiz-actions">
        {!isAnswered ? (
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={selectedAnswer === null}
          >
            Antworten
          </button>
        ) : (
          <button className="btn-primary" onClick={handleNext}>
            {currentIndex < questions.length - 1 ? 'Weiter' : 'Ergebnis'}
          </button>
        )}
      </div>

      {/* Running score */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '20px',
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '14px',
      }}>
        <span style={{ color: '#4caf50' }}>
          ✅ {correctCount}
        </span>
        <span style={{ color: '#ef5350' }}>
          ❌ {wrongIds.length}
        </span>
      </div>
    </div>
  );
}
